import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Users } from "lucide-react";
import { Button } from "@tabipla/ui/components/button";
import { LoadingState, EmptyState } from "@tabipla/ui";
import { MekkeLayout } from "../layouts/MekkeLayout";
import { FollowListItem } from "../components/users/FollowListItem";
import { useFollowers } from "../../application/hooks/useFollow";
import { useUserProfile } from "../../application/hooks/useUserProfile";

export function UserFollowersPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: profile } = useUserProfile(userId);
  const { data: followers, isLoading } = useFollowers(userId ?? "", { limit: 50 });

  if (isLoading) {
    return (
      <MekkeLayout hideNav>
        <div className="min-h-screen pb-6">
          <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="mx-auto flex max-w-4xl items-center gap-3 px-4 py-3">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t("common.back")}
              </Button>
              <h1 className="font-semibold">{t("users.followers")}</h1>
            </div>
          </header>
          <LoadingState message={t("common.loading")} />
        </div>
      </MekkeLayout>
    );
  }

  return (
    <MekkeLayout hideNav>
      <div className="min-h-screen pb-6">
        {/* Header */}
        <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="mx-auto flex max-w-4xl items-center gap-3 px-4 py-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("common.back")}
            </Button>
            <div>
              <h1 className="font-semibold">{t("users.followers")}</h1>
              {profile && <p className="text-xs text-muted-foreground">{profile.name}</p>}
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-4xl">
          {followers && followers.length > 0 ? (
            <div className="divide-y">
              {followers.map((user) => (
                <FollowListItem key={user.id} user={user} />
              ))}
            </div>
          ) : (
            <EmptyState
              icon={Users}
              title={t("users.noFollowers")}
              description={t("users.noFollowersDesc")}
              variant="dashed"
            />
          )}
        </div>
      </div>
    </MekkeLayout>
  );
}
